"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { clearToken, getName } from "@/lib/auth";
import { api } from "@/lib/api";
import { Footer } from "@/components/Footer";
import Logo from "@/components/Logo";
import { InviteFriend } from "@/components/InviteFriend";

type NavKey = "cases" | "progress" | "guide" | "pricing" | "settings";

const NAV_ICONS: Record<NavKey, ReactNode> = {
  cases: (
    <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  ),
  progress: (
    <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  ),
  guide: (
    <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  pricing: (
    <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
    </svg>
  ),
  settings: (
    <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  ),
};

export function AppShell({
  active,
  children,
}: {
  active: NavKey;
  children: ReactNode;
}) {
  const t = useTranslations("sidebar");
  const tTier = useTranslations("dashboard.tier");
  const router = useRouter();
  const [userName, setUserName] = useState("");
  // The membership tier shown next to the user's name in the bottom block.
  // Fetched here (not passed per-page) so it renders on every screen the shell
  // wraps, not just the dashboard.
  const [tier, setTier] = useState<string | null>(null);
  // During the tester-only launch phase nobody can self-serve buy a plan, so the
  // Pricing link is hidden for the Tester tier. Default hidden until the tier is
  // known (so testers never see it flash); it reappears automatically for paid
  // tiers once self-serve plans go live — no further code change needed.
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    setUserName(getName() ?? "");
    api.getSubscriptionStatus()
      .then((s) => {
        setTier(s.tier);
        setShowPricing(s.tier !== "tester");
      })
      .catch(() => setShowPricing(false));
  }, []);

  const tierBadge = tier && (
    <span
      className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${
        tier === "pro" ? "bg-[var(--accent-light)] text-[var(--accent)]" :
        tier === "tester" ? "bg-[var(--accent-light)] text-[var(--accent)]" :
        tier === "trial" ? "text-[var(--accent-warm)]" :
        "bg-gray-100 text-gray-500"
      }`}
      style={tier === "trial" ? { background: "var(--accent-warm-pale)", border: "1px solid rgba(200,135,58,0.25)" } : undefined}
    >
      {tTier(tier)}
    </span>
  );

  function handleLogout() {
    clearToken();
    router.push("/login");
  }

  function navLinkClass(key: NavKey) {
    return `flex items-center gap-2.5 px-3 md:px-5 py-2.5 text-sm border-l-2 transition-colors ${
      key === active
        ? "font-semibold text-[var(--accent)] border-[var(--accent-warm)]"
        : "text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)] hover:bg-[var(--surface-alt)]"
    }`;
  }

  function navLinkStyle(key: NavKey) {
    return key === active ? { background: "rgba(200,135,58,0.12)" } : undefined;
  }

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Sidebar */}
      <aside className="w-14 md:w-56 shrink-0 sticky top-0 h-screen flex flex-col border-r border-[var(--border)]" style={{ background: "var(--sidebar-bg)" }}>
        {/* Logo */}
        <div className="px-3 md:px-5 pt-6 pb-5 border-b border-[var(--border)] shrink-0">
          <Logo href="/dashboard" size={18} className="hidden md:block" />
          <Link
            href="/dashboard"
            className="md:hidden block text-center"
            style={{ fontFamily: "var(--font-lora)", fontSize: "18px", fontWeight: 600 }}
          >
            <span className="text-[var(--text-primary)]">P</span>
            <span className="text-[var(--accent-warm)]">P</span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 pt-3">
          <Link href="/dashboard" title={t("cases")} className={navLinkClass("cases")} style={navLinkStyle("cases")}>
            {NAV_ICONS.cases}
            <span className="hidden md:inline whitespace-nowrap">{t("cases")}</span>
          </Link>
          <Link href="/progress" title={t("progress")} className={navLinkClass("progress")} style={navLinkStyle("progress")}>
            {NAV_ICONS.progress}
            <span className="hidden md:inline whitespace-nowrap">{t("progress")}</span>
          </Link>
          <Link href="/guide" title={t("guide")} className={navLinkClass("guide")} style={navLinkStyle("guide")}>
            {NAV_ICONS.guide}
            <span className="hidden md:inline whitespace-nowrap">{t("guide")}</span>
          </Link>
          {showPricing && (
            <Link href="/pricing" title={t("pricing")} className={navLinkClass("pricing")} style={navLinkStyle("pricing")}>
              {NAV_ICONS.pricing}
              <span className="hidden md:inline whitespace-nowrap">{t("pricing")}</span>
            </Link>
          )}
        </nav>

        {/* Settings — pinned above the bottom block */}
        <div className="shrink-0 border-t border-[var(--border)] py-2">
          <Link href="/settings" title={t("settings")} className={navLinkClass("settings")} style={navLinkStyle("settings")}>
            {NAV_ICONS.settings}
            <span className="hidden md:inline whitespace-nowrap">{t("settings")}</span>
          </Link>
        </div>

        {/* Bottom: user, logout */}
        <div className="shrink-0 border-t border-[var(--border)] px-3 md:px-5 py-4 space-y-3">
          {(userName || tierBadge) && (
            <div className="hidden md:flex items-center justify-between gap-2 min-w-0">
              {userName && (
                <span className="text-xs text-[var(--text-secondary)] truncate">{userName}</span>
              )}
              {tierBadge}
            </div>
          )}
          <button
            onClick={handleLogout}
            title={t("logout")}
            className="flex items-center gap-2.5 w-full text-sm text-[var(--text-muted)] hover:text-red-500 transition-colors"
          >
            <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
            </svg>
            <span className="hidden md:inline">{t("logout")}</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 min-w-0">{children}</div>
        <Footer />
      </div>
    </div>
  );
}
